import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onPlanLimitReached, type PlanLimitInfo } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';

const PLAN_LABEL: Record<string, string> = {
  starter: 'Starter',
  pro: 'Pro',
  scale: 'Scale',
};

/**
 * Modal global de upgrade. Dispara quando qualquer request recebe 402 de
 * plano (FeatureGuard `plan_required` ou enforcement de assentos/unidades
 * `plan_limit_reached`) — fecha o ciclo: em vez de um toast de erro seco, o
 * dono vê o que travou e um caminho claro pra fazer upgrade.
 *
 * Montado uma vez no AppShell. O past-due (subscription_inactive) é tratado
 * à parte pelo PaywallOverlay.
 */
export function UpgradeModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState<PlanLimitInfo | null>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    onPlanLimitReached((payload) => setInfo(payload));
  }, []);

  // Fecha no Esc e move o foco pro botão principal ao abrir (a11y).
  useEffect(() => {
    if (!info) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setInfo(null);
    };
    document.addEventListener('keydown', onKey);
    primaryRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [info]);

  if (!info) return null;

  const canUpgrade = user?.role === 'owner' || user?.role === 'manager';
  const current = info.currentPlan ? PLAN_LABEL[info.currentPlan] ?? info.currentPlan : null;
  const target =
    info.requiredPlan && PLAN_LABEL[info.requiredPlan]
      ? PLAN_LABEL[info.requiredPlan]
      : info.currentPlan === 'starter'
        ? 'Pro'
        : 'Scale';

  const close = () => setInfo(null);
  const goUpgrade = () => {
    close();
    navigate('/configuracoes?section=assinatura');
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-title"
      data-testid="upgrade-modal"
      onClick={close}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <span className="text-2xl" aria-hidden>
            🔒
          </span>
          <div className="flex-1">
            <h2
              id="upgrade-title"
              className="text-base font-semibold text-ink-900"
            >
              Seu plano não permite isso
            </h2>
            <p className="mt-1 text-sm text-ink-600">
              {info.message ??
                'Este recurso exige um plano superior ao atual.'}
            </p>
            {current && (
              <p className="mt-2 text-xs text-ink-500">
                Plano atual: <strong>{current}</strong>.
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            ref={primaryRef}
            type="button"
            onClick={close}
            className="px-3 py-2 rounded-lg text-sm font-medium text-ink-600 hover:bg-ink-50"
          >
            Agora não
          </button>
          {canUpgrade ? (
            <button
              type="button"
              onClick={goUpgrade}
              data-testid="upgrade-cta"
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-brand-600 to-accent-600 text-white hover:opacity-95 shadow-sm"
            >
              Fazer upgrade para {target} →
            </button>
          ) : (
            <span className="text-xs text-ink-500">
              Peça ao proprietário da clínica para fazer o upgrade.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
